import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { LS_USERNAME_KEY } from "@/lib/config";
import { Edit, Spinner, X } from "@/lib/icons";
import { toast } from "sonner";

export function NewCommentDialog({
  uid,
  productName,
  open,
  onClose,
}: {
  uid: string;
  productName: string;
  open: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const isPendingRef = useRef(false);

  const [body, setBody] = useState("");
  const [username, setUsername] = useState(() => localStorage.getItem(LS_USERNAME_KEY) ?? "");
  const [editingUsername, setEditingUsername] = useState(() => !localStorage.getItem(LS_USERNAME_KEY));
  const [bodyError, setBodyError] = useState("");
  const [usernameError, setUsernameError] = useState("");

  // lock body scroll
  useEffect(() => {
    if (!open) return; // ------
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  // focus textarea on open
  useEffect(() => {
    if (open) textareaRef.current?.focus();
  }, [open]);

  // reset form state on open
  useEffect(() => {
    if (!open) return; // ------
    setBody("");
    setBodyError("");
    setUsernameError("");
    setUsername(localStorage.getItem(LS_USERNAME_KEY) ?? "");
    setEditingUsername(!localStorage.getItem(LS_USERNAME_KEY));
  }, [open]);

  // escape key
  useEffect(() => {
    if (!open) return; // ------
    const handler = (e: KeyboardEvent) => { if (e.key === "Escape" && !isPendingRef.current) onClose(); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open, onClose]);

  const add = useMutation({
    mutationFn: (payload: { username: string; body: string }) => api.addComment(uid, payload),
    onSuccess: (comment) => {
      localStorage.setItem(LS_USERNAME_KEY, comment.username);
      qc.invalidateQueries({ queryKey: ["products"] });
      qc.invalidateQueries({ queryKey: ["comments", uid] });
      onClose();
    },
    onError: () => toast.error("Couldn't post comment — check your connection and try again."),
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimBody = body.trim();
    const trimUsername = username.trim();
    let ok = true;
    if (!trimBody) { setBodyError("Comment required"); ok = false; }
    else setBodyError("");
    if (!trimUsername) { setUsernameError("Username required"); ok = false; }
    else setUsernameError("");
    if (!ok) return; // ------
    add.mutate({ username: trimUsername, body: trimBody });
  }

  function handleChangeUser() {
    localStorage.removeItem(LS_USERNAME_KEY);
    setEditingUsername(true);
    setUsername("");
  }

  isPendingRef.current = add.isPending;

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={() => { if (!add.isPending) onClose(); }}
      aria-hidden="true"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="ncd-title"
        className="relative mx-4 w-full max-w-md rounded-xl bg-white p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={() => { if (!add.isPending) onClose(); }}
          aria-label="Close"
          disabled={add.isPending}
          className="absolute right-4 top-4 text-zinc-400 hover:text-zinc-700"
        >
          <X className="size-4" />
        </button>

        <h2 id="ncd-title" className="mb-0.5 text-sm font-semibold text-zinc-800">
          Add a comment
        </h2>
        <p className="mb-4 truncate text-xs text-zinc-500">{productName}</p>

        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <textarea
              ref={textareaRef}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Write a comment…"
              rows={3}
              className="w-full resize-y rounded-md border border-zinc-200 bg-zinc-50 p-2 text-sm outline-none focus:border-zinc-400"
            />
            {bodyError && <p className="mt-0.5 text-xs text-rose-600">{bodyError}</p>}
          </div>

          {editingUsername ? (
            <div>
              <input
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Your name"
                className="w-48 rounded-md border border-zinc-200 bg-zinc-50 p-2 text-sm outline-none focus:border-zinc-400"
              />
              {usernameError && <p className="mt-0.5 text-xs text-rose-600">{usernameError}</p>}
            </div>
          ) : (
            <p className="text-xs text-zinc-500">
              Commenting as <strong>{username}</strong>
              {" · "}
              <button
                type="button"
                onClick={handleChangeUser}
                className="inline-flex items-center gap-1 text-indigo-600 hover:underline"
              >
                <Edit className="size-3" /> change
              </button>
            </p>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={() => { if (!add.isPending) onClose(); }}
              className="rounded-md border border-zinc-200 px-3 py-1.5 text-xs text-zinc-600 hover:bg-zinc-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={add.isPending}
              className="flex items-center gap-1.5 rounded-md bg-zinc-800 px-3 py-1.5 text-xs font-medium text-white hover:bg-zinc-700 disabled:opacity-50"
            >
              {add.isPending && <Spinner className="size-3 animate-spin" />}
              {add.isPending ? "Posting…" : "Post comment"}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body,
  );
}
